import * as XLSX from 'xlsx';
import { extractRawBarcode } from './barcodeUtils';

export const EFFECTS_IMPORT_LIMITS = Object.freeze({
  fileBytes: 5 * 1024 * 1024,
  rows: 1000,
  columns: 100,
  identifierLength: 256,
  effectsLength: 20000,
});

// Also guard rawBarcode because legacy products can expose this exact-match field.
export const EFFECTS_IDENTITY_GUARD_FIELDS = Object.freeze([
  'itemNumber', 'barcode', 'rawBarcode', 'rawStyleBarcodes', 'styleBarcodes',
]);

export const EFFECTS_ROW_LABELS = {
  ready: '可匯入',
  unchanged: '內容相同，不需更新',
  skipped: '已有功效，依設定略過',
  empty: '空白功效，不會清除',
  invalid: '格式錯誤',
  unmatched: '找不到完全相符的商品',
  ambiguous: '符合多筆商品，已阻擋',
  conflict: '重複資料內容衝突，已阻擋',
  duplicate: '相同重複列，不重複寫入',
  applied: '已儲存',
  saveConflict: '商品已變更，請重新預覽',
  failed: '儲存失敗，請重新預覽後再試',
};

const cellText = (cell) => cell?.v === null || cell?.v === undefined ? '' : String(cell.v);
const storedIdentifier = (value) => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isSafeInteger(value)
    && value >= 0 && value < 1e15) return String(value);
  return '';
};

/** Read the original cell instead of sheet_to_json, which can coerce identifiers. */
export const readEffectsIdentifier = (cell) => {
  const value = cellText(cell);
  if (!value.trim()) return { value, error: '識別碼空白' };
  if (cell.f || cell.F) return { value, error: '識別碼含公式；請貼上原始文字識別碼後重新匯入' };
  if (value.length > EFFECTS_IMPORT_LIMITS.identifierLength) {
    return { value, error: `識別碼超過 ${EFFECTS_IMPORT_LIMITS.identifierLength} 字` };
  }
  if (cell.t === 'n' || typeof cell.v === 'number') {
    if (!Number.isSafeInteger(cell.v) || cell.v < 0 || cell.v >= 1e15
      || (cell.z && XLSX.SSF.is_date(cell.z))) {
      return { value, error: '數值識別碼可能已失真（超過 15 位、非整數或日期）；請從原始來源重新取得文字識別碼，不能只變更儲存格格式' };
    }
    // Preserve a numeric cell's explicit zero-padding, but never use rounded scientific display.
    const formatted = String(cell.w ?? XLSX.utils.format_cell(cell));
    const identifier = /^\d+$/.test(formatted) && Number(formatted) === cell.v
      ? formatted : String(cell.v);
    if (identifier.length > EFFECTS_IMPORT_LIMITS.identifierLength) {
      return { value: identifier, error: `格式化識別碼超過 ${EFFECTS_IMPORT_LIMITS.identifierLength} 字` };
    }
    return {
      value: identifier,
      warning: 'Excel 數值欄位可能已遺失前導零；請對照原始識別碼。可見的補零格式已保留',
      numeric: true,
    };
  }
  if (cell.t !== 's' && cell.t !== 'str' && typeof cell.v !== 'string') {
    return { value, error: '識別碼必須是文字或可安全表示的整數' };
  }
  return {
    value,
    warning: value !== value.trim() ? '識別碼前後空白已原樣保留，僅完全相符才會配對' : '',
    numeric: false,
  };
};

export const inspectEffectsSheet = (worksheet, headerRow = 1) => {
  if (!worksheet || !worksheet['!ref']) throw new Error('此工作表沒有資料');
  // sheetRows parsing can truncate !ref; !fullref must still be checked so rows never disappear.
  const range = XLSX.utils.decode_range(worksheet['!fullref'] || worksheet['!ref']);
  if (!Number.isInteger(headerRow) || headerRow < 1 || headerRow > range.e.r + 1) {
    throw new Error('請指定有效的標題列（Excel 列號）');
  }
  if (range.e.c + 1 > EFFECTS_IMPORT_LIMITS.columns) {
    throw new Error(`工作表超過 ${EFFECTS_IMPORT_LIMITS.columns} 欄，請先縮小資料範圍`);
  }
  if (range.e.r + 1 > EFFECTS_IMPORT_LIMITS.rows + 1) {
    throw new Error(`工作表最多支援 ${EFFECTS_IMPORT_LIMITS.rows} 筆資料與 1 列標題，請分批匯入；沒有截斷或匯入任何資料`);
  }
  const columns = [];
  for (let column = 0; column <= range.e.c; column += 1) {
    const letter = XLSX.utils.encode_col(column);
    const text = cellText(worksheet[XLSX.utils.encode_cell({ r: headerRow - 1, c: column })]);
    columns.push({ index: column, letter, label: `${letter} — ${text || '（空白標題）'}` });
  }
  return { columns, lastRow: range.e.r + 1, dataRowCount: Math.max(0, range.e.r + 1 - headerRow) };
};

export const readEffectsSheet = ({ worksheet, headerRow = 1, keyColumn, effectsColumn }) => {
  const sheet = inspectEffectsSheet(worksheet, headerRow);
  if (!Number.isInteger(keyColumn) || !Number.isInteger(effectsColumn)
    || keyColumn < 0 || effectsColumn < 0
    || keyColumn >= sheet.columns.length || effectsColumn >= sheet.columns.length) {
    throw new Error('請選擇識別碼欄與功效欄');
  }
  if (keyColumn === effectsColumn) throw new Error('識別碼欄與功效欄不可相同');
  const rows = [];
  for (let rowNumber = headerRow + 1; rowNumber <= sheet.lastRow; rowNumber += 1) {
    const keyCell = worksheet[XLSX.utils.encode_cell({ r: rowNumber - 1, c: keyColumn })];
    const effectsCell = worksheet[XLSX.utils.encode_cell({ r: rowNumber - 1, c: effectsColumn })];
    const identifier = readEffectsIdentifier(keyCell);
    const effects = cellText(effectsCell);
    let error = identifier.error || '';
    if (effectsCell?.f || effectsCell?.F) error = error || '功效欄含公式，請貼上文字值後重新匯入';
    if (effectsCell?.t === 'e') error = error || '功效欄是 Excel 錯誤值，請修正後重新匯入';
    if (effects.length > EFFECTS_IMPORT_LIMITS.effectsLength) {
      error = error || `功效超過 ${EFFECTS_IMPORT_LIMITS.effectsLength} 字，請縮短後匯入`;
    }
    rows.push({
      rowNumber,
      key: identifier.value,
      effects,
      warning: identifier.warning || '',
      numeric: Boolean(identifier.numeric),
      error,
    });
  }
  return rows;
};

export const productEffectsKeys = (product, keyField) => {
  if (keyField === 'itemNumber') return [storedIdentifier(product.itemNumber)].filter(Boolean);
  if (keyField !== 'barcode') throw new Error('只支援料號或條碼完全相符配對');
  const raw = Array.isArray(product.rawStyleBarcodes) ? product.rawStyleBarcodes : [];
  const combined = Array.isArray(product.styleBarcodes) ? product.styleBarcodes : [];
  const values = [product.barcode, product.rawBarcode, ...raw, ...combined];
  combined.forEach((barcode, index) => {
    // Older products only have combined barcodes. Prefer explicit raw values where available.
    if (!storedIdentifier(raw[index]) && storedIdentifier(barcode)) {
      values.push(extractRawBarcode(storedIdentifier(barcode), storedIdentifier(product.itemNumber)));
    }
  });
  return [...new Set(values.map(storedIdentifier).filter(Boolean))];
};

/** All input rows survive the preview, including duplicates and blank/malformed rows. */
export const buildEffectsPreview = ({ rows, products, keyField, existingPolicy }) => {
  if (!['itemNumber', 'barcode'].includes(keyField)) throw new Error('請選擇料號或條碼配對');
  if (!['skip', 'overwrite'].includes(existingPolicy)) throw new Error('請明確選擇略過或覆寫既有功效');
  if (rows.length > EFFECTS_IMPORT_LIMITS.rows) throw new Error('資料超過單次匯入筆數上限');
  const productIndex = new Map();
  products.forEach((product) => {
    if (!product.id) return;
    productEffectsKeys(product, keyField).forEach((key) => {
      if (!productIndex.has(key)) productIndex.set(key, new Map());
      productIndex.get(key).set(product.id, product);
    });
  });
  const preview = rows.map((row) => {
    const matches = [...(productIndex.get(row.key)?.values() || [])];
    const product = matches.length === 1 ? matches[0] : null;
    const existingEffects = product?.effects === null || product?.effects === undefined
      ? '' : String(product.effects);
    let status = 'ready';
    let message = '';
    if (row.error) { status = 'invalid'; message = row.error; }
    else if (!row.effects.trim()) status = 'empty';
    else if (!matches.length) status = 'unmatched';
    else if (matches.length > 1) { status = 'ambiguous'; message = `符合 ${matches.length} 筆商品，請先修正重複識別碼或改用另一種識別碼`; }
    else if (row.effects === existingEffects) status = 'unchanged';
    else if (existingEffects.trim() && existingPolicy === 'skip') status = 'skipped';
    return { ...row, keyField, status, message, matches, product, existingEffects };
  });

  // Repeated keys and different barcode aliases targeting one product must agree.
  const groups = new Map();
  preview.forEach((row) => {
    if (row.error || !row.effects.trim()) return;
    const groupKey = row.product ? `product:${row.product.id}` : `key:${row.key}`;
    if (!groups.has(groupKey)) groups.set(groupKey, []);
    groups.get(groupKey).push(row);
  });
  groups.forEach((group) => {
    if (group.length < 2) return;
    const sourceRows = group.map((row) => row.rowNumber).join('、');
    if (new Set(group.map((row) => row.effects)).size > 1) {
      group.forEach((row) => {
        row.status = 'conflict';
        row.message = `第 ${sourceRows} 列指向相同識別碼或商品，但功效不同；請修正來源檔後重新預覽`;
      });
    } else {
      group.forEach((row, index) => {
        row.message = `第 ${sourceRows} 列功效完全相同；僅第 ${group[0].rowNumber} 列可依配對結果處理`;
        if (index > 0 && ['ready', 'skipped', 'unchanged'].includes(row.status)) row.status = 'duplicate';
      });
    }
  });
  return preview;
};

export const summarizeEffectsPreview = (rows) => rows.reduce((summary, row) => {
  summary[row.status] = (summary[row.status] || 0) + 1;
  return summary;
}, {});

/** Persistence is injected so the import logic can be tested without any backend access. */
export const applyEffectsPreview = async ({ rows, country, lookupProductsByKey, saveProductPatch, onRowResult }) => {
  const results = rows.map((row) => ({ ...row }));
  for (let index = 0; index < results.length; index += 1) {
    const row = results[index];
    if (row.status !== 'ready' || !row.product?.id || !row.effects.trim()) continue;
    try {
      if (typeof lookupProductsByKey !== 'function') throw new Error('無法重新驗證商品識別碼，未儲存；請重新預覽後再試');
      const candidates = await lookupProductsByKey({ country, keyField: row.keyField, key: row.key, base: row.product });
      if (!Array.isArray(candidates)) throw new Error('商品識別碼查詢結果無效，未儲存；請重新預覽後再試');
      const exactMatches = [...new Map(candidates
        .filter((candidate) => candidate.id && productEffectsKeys(candidate, row.keyField).includes(row.key))
        .map((candidate) => [candidate.id, candidate])).values()];
      if (exactMatches.length !== 1 || exactMatches[0].id !== row.product.id) {
        throw Object.assign(new Error('識別碼配對已變更、消失或符合多筆商品，未儲存；請重新預覽並核對配對'), { code: 'product-conflict' });
      }
      await saveProductPatch({
        country,
        id: row.product.id,
        base: row.product,
        patch: { effects: row.effects },
        guardFields: [...EFFECTS_IDENTITY_GUARD_FIELDS],
      });
      row.status = 'applied';
      row.message = '已儲存功效；其餘欄位不受影響';
    } catch (error) {
      const isConflict = /conflict|changed|not-found|not_found/i.test(error.code || '')
        || error.name === 'ProductConflictError';
      row.status = isConflict ? 'saveConflict' : 'failed';
      row.message = error.message || '無法儲存，請重新讀取商品並預覽後再試';
    }
    if (onRowResult) onRowResult({ ...row }, index);
  }
  return results;
};
